import { api } from "./client";
import type {
  OrderStatusCountVO,
  OrderCraDTO,
  OrderBasicDTO,
  CreateBasicOrderRequest,
  CreateBasicOrderResponse,
  PolicyholderDTO,
  OrderEffectivePolicyholderVO,
  InsuredDTO,
  BeneficiaryDTO,
  FinancialDataDTO,
  OrderProductPlanVO,
  HighPremiumOrder,
  InsurerSyncPreviewVO,
} from "./types/order";
import type { OrderFnaDTO } from "./types/order.js";

/**
 * 查询订单状态数量
 * GET /api/v1/advisor/order/count-by-status
 */
export function countOrdersByStatus(): Promise<OrderStatusCountVO[]> {
  return api.get<OrderStatusCountVO[]>("/advisor/order/count-by-status");
}

/**
 * 发送订单确认
 * PUT /api/v1/advisor/order/send-confirmation
 * @param orderId 订单ID
 * @param sendEmail 是否发送邮件
 */
export function sendOrderConfirmation(orderId: number, sendEmail: boolean): Promise<string> {
  return api.put<string>("/advisor/order/send-confirmation", undefined, {
    params: { orderId, sendEmail },
  });
}

/**
 * 获取当前生效投保人信息
 * GET /api/v1/advisor/order/effective-policyholder
 */
export function getEffectivePolicyholder(orderId: number): Promise<OrderEffectivePolicyholderVO> {
  return api.get<OrderEffectivePolicyholderVO>("/advisor/order/effective-policyholder", {
    params: { orderId },
  });
}

/**
 * 查询同步保司预览信息
 * GET /api/v1/advisor/order/sync-insurer-preview
 */
export function getInsurerSyncPreview(orderId: number): Promise<InsurerSyncPreviewVO> {
  return api.get<InsurerSyncPreviewVO>("/advisor/order/sync-insurer-preview", {
    params: { orderId },
  });
}

/**
 * 同步保司订单
 * PUT /api/v1/advisor/order/sync-insurer
 */
export function syncInsurerOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/sync-insurer", undefined, {
    params: { orderId },
  });
}

/**
 * 保存客户风险评估问卷
 * PUT /api/v1/advisor/order/save-cra
 * @param orderId 订单ID
 * @param sendEmail 是否发送邮件
 */
export function saveCustomerRiskAssessment(orderCraDTO: OrderCraDTO): Promise<string> {
  return api.put<string>("/advisor/order/save-cra", orderCraDTO);
}

/**
 * 保存客户风险评估问卷的持有保单
 * PUT /api/v1/advisor/order/save-cra-insured-policy
 * @param data 持有保单数据
 */
export function saveCraInsuredPolicy(data: { highPremiumOrders?: HighPremiumOrder[]; orderId?: number }): Promise<string> {
  return api.put<string>("/advisor/order/save-cra-insured-policy", data);
}

/**
 * 创建订单
 * POST /api/v1/advisor/order/create
 */
export function createOrder(data: OrderBasicDTO): Promise<number> {
  return api.post<number>("/advisor/order/create", data);
}

/**
 * 新增訂單/投保人/新增客戶檔案
 * POST /api/v1/advisor/order/create-basic
 */
export function createBasicOrder(data: CreateBasicOrderRequest): Promise<CreateBasicOrderResponse> {
  return api.post<CreateBasicOrderResponse>("/advisor/order/create-basic", data);
}

/**
 * 更新业务资料 / 变更顾问（顾问端）
 * PUT /api/v1/advisor/order/update-referrer
 */
export function updateAdvisorOrderReferrer(
  orderId: number,
  referrerUid?: number,
): Promise<boolean> {
  return api.put<boolean>("/advisor/order/update-referrer", undefined, {
    params: { orderId, referrerUid },
  });
}

/**
 * 更新投保人
 * PUT /api/v1/advisor/order/update-policyholder
 */
export function updatePolicyholder(_orderId: number, data: PolicyholderDTO): Promise<boolean> {
  const payload = {
    ...data,
    basic: data?.basic,
    personal: data?.personal,
    tax: data?.tax,
    work: data?.work,
    familyMembers: data?.familyMembers,
    orderId: _orderId,
    policyholderId: data.policyholderId,
  };
  return api.put<boolean>("/advisor/order/update-policyholder", payload);
}

/**
 * 更新第二投保人
 * PUT /api/v1/advisor/order/update-second-policyholder
 */
export function updateSecondaryPolicyholder(_orderId: number, data?: PolicyholderDTO, isDelete?: boolean): Promise<boolean> {
  if (isDelete) {
    return api.put<boolean>("/advisor/order/update-second-policyholder", { orderId: _orderId, draft: false, submit: false, secondEnabled: false });
  }
  const payload = {
    ...data,
    basic: data?.basic,
    personal: data?.personal,
    tax: data?.tax,
    work: data?.work,
    familyMembers: data?.familyMembers,
    orderId: _orderId,
    policyholderId: data.policyholderId,
    secondEnabled: true,
  };
  return api.put<boolean>("/advisor/order/update-second-policyholder", payload);
}

/**
 * 更新受保人
 * PUT /api/v1/advisor/order/update-insured
 */
export function updateInsured(_orderId: number, data: InsuredDTO): Promise<boolean> {
  const payload = {
    ...data,
    basic: data.basic ?? null,
    personal: data.personal ?? null,
    relationship: data.relationship ?? null,
    tax: data.tax ?? null,
    orderId: _orderId,
    insuredId: data.insuredId,
  };
  return api.put<boolean>("/advisor/order/update-insured", payload);
}

/**
 * 更新第二受保人
 * PUT /api/v1/advisor/order/update-second-insured
 */
export function updateSecondInsured(_orderId: number, data: InsuredDTO, isDelete?: boolean): Promise<boolean> {
  if (isDelete) {
    return api.put<boolean>("/advisor/order/update-second-insured", { orderId: _orderId, draft: false, submit: false, secondEnabled: false });
  }
  const payload = {
    ...data,
    basic: data.basic ?? null,
    personal: data.personal ?? null,
    relationship: data.relationship ?? null,
    tax: data.tax ?? null,
    orderId: _orderId,
    insuredId: data.insuredId,
    secondEnabled: true,
  };
  return api.put<boolean>("/advisor/order/update-second-insured", payload);
}

/**
 * 更新受益人
 * PUT /api/v1/advisor/order/update-beneficiary
 */
export function updateBeneficiary(_orderId: number, data: BeneficiaryDTO): Promise<boolean> {
  return api.put<boolean>("/advisor/order/update-beneficiary", data);
}

/**
 * 删除受益人
 * PUT /api/v1/advisor/order/delete-beneficiary
 */
export function deleteBeneficiary(orderId: number, beneficiaryId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/delete-beneficiary", undefined, {
    params: { orderId, beneficiaryId },
  });
}

/**
 * 更新财务资料
 * PUT /api/v1/advisor/order/update-financial
 */
export function updateFinancial(_orderId: number, data: FinancialDataDTO): Promise<boolean> {
  const payload = { ...data, orderId: _orderId };
  return api.put<boolean>("/advisor/order/update-financial", payload);
}

/**
 * 更新财务需求分析
 * PUT /api/v1/advisor/order/update-fna
 */
export function updateFinancialAnalysis(_orderId: number, data: OrderFnaDTO): Promise<boolean> {
  const payload = { ...data, orderId: _orderId };
  return api.put<boolean>("/advisor/order/update-fna", payload);
}

/**
 * 更新订单产品信息
 * PUT /api/v1/advisor/order/product
 */
export function updateOrderProduct(data: OrderProductPlanVO): Promise<boolean> {
  const payload = {
    ...data,
    isPrepaid: data.isPrepaid ?? false,
    isPremiumFinanced: data.isPremiumFinanced ?? false,
    isPolicyBackdated: data.isPolicyBackdated ?? false,
    applySpecialFirstYearDiscount: data.applySpecialFirstYearDiscount ?? false,
    isIncreasingProtectionIncluded: data.isIncreasingProtectionIncluded ?? false,
    isDdaSupported: data.isDdaSupported ?? false,
  };
  return api.put<boolean>("/advisor/order/product", payload);
}

/**
 * 删除订单产品信息
 * PUT /api/v1/advisor/order/delete-product
 */
export function deleteOrderProduct(orderId: number, productId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/delete-product", undefined, {
    params: { orderId, productId },
  });
}

/**
 * 提交订单
 * PUT /api/v1/advisor/order/submit-order
 */
export function submitOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/submit-order", undefined, { params: { orderId } });
}

/**
 * 提交订单签署
 * PUT /api/v1/advisor/order/signature
 */
export function submitOrderSignature(data: OrderSignatureDTO): Promise<boolean> {
  return api.put<boolean>("/advisor/order/signature", data);
}

/**
 * 签署订单 (确认完成)
 * PUT /api/v1/advisor/order/signed-order
 * @param orderId 订单ID
 */
export function confirmOrderSigned(orderId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/signed-order", undefined, {
    params: { orderId },
  });
}

/**
 * 获取订单列表（分页）
 * GET /api/v1/order/list
 * 使用 Spring Data 标准分页响应格式
 */
export function getOrderList(params: OrderListRequest, config?: RequestConfig) {
  return api.get<PageOrderListVO>("/advisor/order/list", { ...config, params });
}

/**
 * 删除订单
 * PUT /api/v1/advisor/order/delete
 */
export function deleteAdvisorOrder(orderId: number): Promise<boolean> {
  return api.put<boolean>("/advisor/order/delete", undefined, { params: { orderId } });
}
